/**
 * Traduz um `ScreenNode` para o `WebhookField` que o `webhook-common` espera.
 *
 * É a fronteira da seção 4.5 do plano: UMA função pura e um renderer, no lugar
 * do `webhook-adapter.component.ts` (1.213 linhas), dos três conversores do
 * service (`convertNodeToWebhookField`, `mapBuilderTypeToWebhookType`,
 * `convertWebhookFieldToNode` — linhas 1362, 1659 e 1688) e das ~15 referências
 * a `app-webhook-adapter-beta` espalhadas pelo HTML do canvas.
 *
 * Por que reusar em vez de escrever 19 widgets do zero: o `webhook-common`
 * despacha para 18 componentes de campo já prontos, testados em produção pelo
 * webhook-creator, e a fronteira dele é estreita — 8 entradas e 1 saída. Cada
 * descriptor de campo aponta para este renderer no começo; quando um widget
 * nativo ficar pronto, muda UMA linha no mapa de renderers, e o rollback é
 * reverter essa linha.
 *
 * A volta (`convertWebhookFieldToNode`, ~390 linhas) não existe aqui: ela só
 * servia para importar configuração de webhook, e virou o migrador do formato
 * `webhookConfiguration`. Saiu do runtime.
 */
import { NodeType, ScreenNode } from '../../model/node-props';
import { GRID_COLUMNS } from '../../model/layout';
import type { WebhookField } from '../../../webhook-creator/webhook-creator/webhook-interfaces/configuration';

/**
 * Tipos que o `webhook-common` sabe desenhar, mapeados a partir dos nossos.
 *
 * `dropdown` é o único que não casa pelo nome: o webhook não tem um tipo
 * separado para ele, e a diferença entre `select` e `dropdown` no modelo antigo
 * era de aparência, não de comportamento — os dois escolhem um valor de uma
 * lista. Some no `select`, e o `showKey` preserva a única diferença visível.
 */
const TYPE_BY_NODE_TYPE: Partial<Record<NodeType, string>> = {
  text: 'text',
  textarea: 'textarea',
  number: 'number',
  date: 'date',
  switch: 'switch',
  select: 'select',
  dropdown: 'select',
  querySelect: 'querySelect',
  keyValue: 'keyValue',
  keyValueObject: 'keyValueObject',
  tag: 'tag',
  search: 'search',
  button: 'button',
  simpletable: 'simpletable',
  table: 'table'
};

/** O `webhook-common` sabe desenhar este tipo? */
export function isBridgeableType(type: NodeType): boolean {
  return TYPE_BY_NODE_TYPE[type] !== undefined;
}

export function bridgedType(type: NodeType): string | undefined {
  return TYPE_BY_NODE_TYPE[type];
}

/**
 * Rótulo do campo, já traduzido.
 *
 * A tradução entra por parâmetro em vez de o conversor injetar o serviço: assim
 * a função continua pura e testável, e quem chama (o renderer) decide como
 * traduzir. Sem isso, testar a conversão exigiria TestBed.
 */
export type TranslateFn = (key: string, params?: object) => string;

export interface BridgeOptions {
  readonly translate: TranslateFn;
  /**
   * Nome do controle no formulário. Por padrão é o id do nó — ver
   * `ScreenFormService`, que monta o formulário plano por id.
   */
  readonly controlName?: string;
}

/**
 * Converte um nó de campo no `WebhookField` correspondente.
 *
 * Só lê o nó; não toca em formulário nem em serviço. O resultado é sempre um
 * objeto novo, então o renderer pode memoizar por identidade e o `ngOnChanges`
 * do `webhook-common` dispara só quando algo mudou de verdade.
 */
export function nodeToWebhookField(node: ScreenNode, options: BridgeOptions): WebhookField {
  const type = bridgedType(node.type);

  if (type === undefined) {
    throw new Error(
      'nodeToWebhookField: o tipo "' + node.type + '" não é desenhado pelo webhook-common'
    );
  }

  const props = node.props as Record<string, unknown>;
  const translate = options.translate;

  const field: WebhookField = {
    field: options.controlName ?? node.id,
    type,
    label: readLabel(props, translate),
    ...readCommonState(props),
    ...readLayout(node),
    ...readTextOptions(props),
    ...readNumberOptions(props),
    ...readChoiceOptions(props, translate),
    ...readButtonOptions(props, translate),
    ...readDateOptions(props),
    ...readNestedObjects(node, props, translate),

    // A margem do `nz-form-item` e SEMPRE sobra aqui: quem separa os campos e a
    // grade de 24 colunas do motor, com `gap`. Somadas, davam 32px entre uma
    // linha e a seguinte — o "um componente muito longe do outro".
    //
    // Os widgets ja tinham a saida (`field.noFormItemMarginBottom`); a ponte e
    // que nunca preenchia.
    noFormItemMarginBottom: true
  } as WebhookField;

  return field;
}

/**
 * Os objetos que os widgets antigos desreferenciam SEM checar.
 *
 * Tres deles, e todos com o mesmo modo de falhar: o template escreve
 * `field.tableConfiguration.size`, e se o objeto for `undefined` a expressao
 * estoura DENTRO do template. Erro de template no Angular nao fica contido —
 * ele aborta a renderizacao da arvore inteira, e o resultado e a tela preta.
 *
 * Por isso eles nascem COMPLETOS, com valor para cada membro lido. Um objeto
 * pela metade tem exatamente o mesmo efeito que nenhum objeto.
 */
function readNestedObjects(
  node: ScreenNode,
  props: Record<string, unknown>,
  translate: TranslateFn
): Partial<WebhookField> {

  switch (node.type) {
    // Os casts passam por `unknown` porque o tipo do webhook descreve estes
    // objetos com precisao, e o que montamos aqui e o MINIMO que os templates
    // leem. Preencher a forma inteira seria copiar interfaces que so o widget
    // usa — e a Fase 6 as apaga quando cada campo ganhar widget nativo.
    case 'button':
      return { action: { icon: asText(props['icon']) } } as unknown as Partial<WebhookField>;

    case 'table':
    case 'simpletable':
      return {
        tableConfiguration: tableOf(props, node.type === 'table', translate)
      } as unknown as Partial<WebhookField>;

    case 'section':
      return { sectionConfiguration: { fields: [] } } as unknown as Partial<WebhookField>;

    default:
      return {};
  }
}

/** A tabela, com todos os membros que o widget le. */
function tableOf(
  props: Record<string, unknown>,
  isFullTable: boolean,
  translate: TranslateFn
): Record<string, unknown> {

  const colunas = Array.isArray(props['columns']) ? props['columns'] : [];

  return {
    fields: colunas.map(coluna => columnToWidgetField(coluna, isFullTable, translate)),
    layout: 'fixed',
    scroll: null,
    size: 'small',
    showPagination: props['paginated'] === true,
    showLoadData: false,
    loadDataConfiguration: null
  };
}

/** O vocabulario de tipo do modelo novo -> o do widget. */
const WIDGET_TYPE_BY_COLUMN_TYPE: Readonly<Record<string, string>> = {
  text: 'text',
  number: 'number',
  date: 'date',
  boolean: 'switch',
  search: 'search',
  dropdown: 'select'
};

/**
 * Uma coluna, no vocabulario do widget.
 *
 * O texto sai TRADUZIDO daqui, e nao como chave: o widget aplica o proprio
 * `translateTA`, que devolve a entrada quando ela nao e chave conhecida — entao
 * texto pronto atravessa intacto, e chave nao traduzida apareceria crua.
 */
function columnToWidgetField(
  coluna: unknown,
  isFullTable: boolean,
  translate: TranslateFn
): Record<string, unknown> {

  const c = (coluna ?? {}) as Record<string, unknown>;
  const tipo = asText(c['type']);

  const campo: Record<string, unknown> = {
    field: asText(c['field']),
    label: translate(asText(c['headerKey'])),
    type: WIDGET_TYPE_BY_COLUMN_TYPE[tipo] ?? 'text',
    hide: false,
    hidden: false,
    // Uma linha so no painel de edicao. O que importa e nao ser `undefined`:
    // o widget monta `formRows` a partir dos valores distintos deste campo.
    row: 0,
    // Cabecalho com uma celula. O widget da tabela le a largura DAQUI, e nao de
    // `colSpan` — que la e a largura no painel de edicao.
    gridConfig: null,
    options: Array.isArray(c['options']) ? c['options'] : [],
    readonly: c['editable'] === false
  };

  if (isFullTable) {
    // Cada campo ocupa a linha inteira do painel de edicao (24 de 24).
    // Na tabela SIMPLES esta mesma propriedade vira o `colspan` do `<th>` e do
    // `<td>`, e um valor de grade faria uma coluna cobrir as vizinhas.
    campo['colSpan'] = 24;
  }

  return campo;
}

function asText(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

// ---------------------------------------------------------------- pedaços

function readLabel(props: Record<string, unknown>, translate: TranslateFn): string {
  // `titleKey` entra junto porque nem todo tipo chama o proprio texto de
  // `labelKey`: o divisor e a secao guardam em `titleKey`, e o divisor ficava
  // SEM titulo por causa disso — o usuario digitava e nada aparecia.
  for (const chave of ['labelKey', 'titleKey']) {
    const valor = props[chave];

    if (typeof valor === 'string' && valor.length > 0) {
      return translate(valor);
    }
  }

  return '';
}

function readCommonState(props: Record<string, unknown>): Partial<WebhookField> {
  const partial: Partial<WebhookField> = {};

  if (props['required'] === true) {
    partial.required = true;
  }
  if (props['readonly'] === true) {
    // O webhook chama de `isDisabled`; no modelo é `readonly`. O efeito visual é
    // o mesmo, e o valor continua indo no save — ver `collectValues`.
    partial.isDisabled = true;
  }
  if (props['hidden'] === true) {
    partial.hidden = true;

    // O MESMO estado, com dois nomes. O `query-select` testa `field.hide` e
    // todos os outros testam `field.hidden` — preencher so um fazia esconder
    // funcionar em quinze tipos e falhar num.
    (partial as Record<string, unknown>)['hide'] = true;
  }
  if (props['defaultValue'] !== undefined) {
    partial.defaultValue = props['defaultValue'];
  }

  return partial;
}

/**
 * A largura vai junto porque alguns widgets do webhook a usam internamente.
 * A posição de verdade continua sendo do container (`child-layout.ts`) — aqui é
 * só informação, não comando.
 */
function readLayout(node: ScreenNode): Partial<WebhookField> {
  const layout = node.layout;

  if (layout === undefined) {
    return {};
  }

  const partial: Partial<WebhookField> = {};

  if (layout.colSpan !== undefined && Number.isFinite(layout.colSpan)) {
    partial.colSpan = clampSpan(layout.colSpan);
  }
  if (layout.rowSpan !== undefined && Number.isFinite(layout.rowSpan)) {
    partial.rowSpan = Math.max(1, Math.floor(layout.rowSpan));
  }

  return partial;
}

function clampSpan(colSpan: number): number {
  const rounded = Math.round(colSpan);
  if (rounded < 1) {
    return 1;
  }
  if (rounded > GRID_COLUMNS) {
    return GRID_COLUMNS;
  }
  return rounded;
}

function readTextOptions(props: Record<string, unknown>): Partial<WebhookField> {
  const partial: Partial<WebhookField> = {};

  const placeholderKey = props['placeholderKey'];
  if (typeof placeholderKey === 'string' && placeholderKey.length > 0) {
    // Sem tradução aqui: quem traduz é `nodeToWebhookField`, e o placeholder
    // passa pelo mesmo caminho — ver `withTranslatedText`.
    partial.placeholder = placeholderKey;
  }

  if (typeof props['maxLength'] === 'number') {
    partial.maxLength = props['maxLength'] as number;
  }
  if (typeof props['minLength'] === 'number') {
    partial.minLength = props['minLength'] as number;
  }
  if (typeof props['rows'] === 'number') {
    partial.minRows = props['rows'] as number;
  }
  if (typeof props['maxRows'] === 'number') {
    partial.maxRows = props['maxRows'] as number;
  }
  if (props['isPassword'] === true) {
    partial.isPassword = true;
  }

  return partial;
}

function readNumberOptions(props: Record<string, unknown>): Partial<WebhookField> {
  const partial: Partial<WebhookField> = {};

  if (typeof props['min'] === 'number') {
    partial.min = props['min'] as number;
  }
  if (typeof props['max'] === 'number') {
    partial.max = props['max'] as number;
  }
  if (typeof props['step'] === 'number') {
    partial.step = props['step'] as number;
  }
  if (typeof props['precision'] === 'number') {
    partial.precision = props['precision'] as number;
  }
  if (typeof props['decimalPlaces'] === 'number') {
    partial.decimalPlaces = props['decimalPlaces'] as number;
  }
  if (props['formatThousands'] === true) {
    partial.formatThousands = true;
  }
  if (typeof props['prefix'] === 'string') {
    partial.addonBefore = props['prefix'] as string;
  }
  if (typeof props['suffix'] === 'string') {
    partial.addonAfter = props['suffix'] as string;
  }

  return partial;
}

/**
 * Opções de lista.
 *
 * O rótulo de cada opção passa pelo tradutor, e o valor NÃO — o valor é dado, e
 * traduzir dado é como se perde o vínculo com o banco.
 */
function readChoiceOptions(
  props: Record<string, unknown>,
  translate: TranslateFn
): Partial<WebhookField> {

  const partial: Partial<WebhookField> = {};
  const options = props['options'];

  if (Array.isArray(options)) {
    const converted: { value: string | number; label: string }[] = [];

    for (const option of options) {
      if (option === null || typeof option !== 'object') {
        // Lista de primitivos: o próprio valor é o rótulo.
        converted.push({ value: option as string | number, label: String(option) });
        continue;
      }

      const record = option as Record<string, unknown>;
      const value = record['value'] as string | number;
      const labelKey = record['labelKey'];

      let label: string;
      if (typeof labelKey === 'string' && labelKey.length > 0) {
        label = translate(labelKey);
      } else if (typeof record['label'] === 'string') {
        label = record['label'] as string;
      } else {
        label = String(value);
      }

      converted.push({ value, label });
    }

    partial.options = converted;
  }

  if (props['showKey'] === true) {
    partial.showKey = true;
  }

  return partial;
}

function readButtonOptions(
  props: Record<string, unknown>,
  translate: TranslateFn
): Partial<WebhookField> {

  const partial: Partial<WebhookField> = {};

  const textKey = props['textKey'];
  if (typeof textKey === 'string' && textKey.length > 0) {
    partial.text = translate(textKey);
  }
  if (typeof props['buttonType'] === 'string') {
    partial.buttonType = props['buttonType'] as string;
  }
  if (typeof props['icon'] === 'string') {
    partial.icon = props['icon'] as string;
  }
  if (typeof props['actionType'] === 'string') {
    partial.actionType = props['actionType'] as string;
  }
  if (props['flowConfig'] !== undefined) {
    partial.flowConfig = props['flowConfig'];
  }

  return partial;
}

function readDateOptions(props: Record<string, unknown>): Partial<WebhookField> {
  const partial: Partial<WebhookField> = {};

  if (typeof props['format'] === 'string') {
    partial.dateFormat = props['format'] as string;
  }

  // O widget le `dateShowTime`; o modelo chama de `showTime`. Sem a traducao, a
  // opcao existia no inspector e o campo nunca mostrava hora.
  if (props['showTime'] === true) {
    (partial as Record<string, unknown>)['dateShowTime'] = true;
  }

  return partial;
}

/**
 * Traduz os textos que ficaram como chave dentro do campo já convertido.
 *
 * Existe separado porque `placeholder` e `tooltip` são texto livre no
 * `WebhookField`, sem par de chave — então a tradução precisa acontecer depois
 * da montagem, e não no meio dela.
 */
export function withTranslatedText(field: WebhookField, translate: TranslateFn): WebhookField {
  const translated: WebhookField = { ...field };

  if (typeof field.placeholder === 'string' && field.placeholder.length > 0) {
    translated.placeholder = translate(field.placeholder);
  }
  if (typeof field.tooltip === 'string' && field.tooltip.length > 0) {
    translated.tooltip = translate(field.tooltip);
  }

  return translated;
}
